import { useState } from "react";
import { useAuth } from "../lib/auth/useAuth";
import { useRole } from "../lib/auth/useRole";
import { useUiStore, type NotifPrefs } from "../state/uiStore";
import { useLists } from "../hooks/useTasks";
import { Avatar } from "../components/Avatar";
import { Segmented } from "../components/ui/Segmented";
import { PRIO, type Priority } from "../lib/design";
import type { TaskVisibility } from "../api/tm";

const NOTIF_TOGGLES: {
  key: keyof NotifPrefs;
  label: string;
  event: string;
  /** false = pas encore dérivable côté client (inbox IC à venir). */
  live: boolean;
}[] = [
  {
    key: "overdue",
    label: "Tâche en retard",
    event: "tasks.task.overdue",
    live: true,
  },
  {
    key: "due_today",
    label: "Échéance aujourd'hui",
    event: "tasks.task.due_today",
    live: true,
  },
  {
    key: "due_soon",
    label: "Échéance proche (3 j)",
    event: "tasks.task.due_soon",
    live: true,
  },
  {
    key: "assigned",
    label: "Assignation reçue",
    event: "tasks.assignment.created",
    live: false,
  },
  {
    key: "mention",
    label: "Mention dans un commentaire",
    event: "tasks.comment.mention",
    live: false,
  },
  {
    key: "recurrence",
    label: "Occurrence récurrente créée",
    event: "tasks.task.recurrence_generated",
    live: false,
  },
];

const selectStyle = {
  font: "inherit",
  border: "1px solid var(--border-3)",
  borderRadius: 6,
  padding: "3px 8px",
  background: "var(--bg)",
} as const;

export function ProfilScreen() {
  const { user, logout } = useAuth();
  const role = useRole();
  const { prefs, setPrefs } = useUiStore();
  const lists = useLists();
  const [tab, setTab] = useState<"profil" | "prefs" | "notifs">("profil");

  const p = user?.profile as Record<string, unknown> | undefined;
  const name = (p?.name as string) ?? (p?.preferred_username as string) ?? "—";
  const email = (p?.email as string) ?? "—";

  return (
    <>
      <div className="view-header">
        <div className="view-title">Profil</div>
        <div className="view-caption">
          Identité Keycloak · préférences locales
        </div>
        <div style={{ marginTop: 10 }}>
          <Segmented
            options={[
              { value: "profil", label: "Profil" },
              { value: "prefs", label: "Préférences" },
              { value: "notifs", label: "Notifications" },
            ]}
            value={tab}
            onChange={setTab}
            ariaLabel="Onglets du profil"
          />
        </div>
      </div>
      <div className="main-scroll" style={{ maxWidth: 640 }}>
        {tab === "profil" && (
          <>
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: 12,
                marginTop: 18,
              }}
            >
              <Avatar name={name} size={40} />
              <div>
                <div style={{ fontWeight: 700, fontSize: 16 }}>{name}</div>
                <div style={{ fontSize: 12, color: "var(--ink-faint)" }}>
                  {email}
                </div>
              </div>
              <button
                className="btn"
                style={{ marginLeft: "auto" }}
                onClick={() => void logout()}
              >
                Se déconnecter
              </button>
            </div>

            <div className="detail-section">
              <div className="detail-section-title">Identité</div>
              <div className="prop-row">
                <span className="prop-label">Nom complet</span>
                <span className="prop-value">{name}</span>
              </div>
              <div className="prop-row">
                <span className="prop-label">Email</span>
                <span className="prop-value">{email}</span>
              </div>
              <div className="prop-row">
                <span className="prop-label">Rôle</span>
                <span className="prop-value">
                  <span className="tag">{role ?? "worker"}</span>
                  <span
                    style={{
                      fontSize: 11,
                      color: "var(--ink-ghost)",
                      marginLeft: 8,
                    }}
                  >
                    ATR · défini par l'admin Todo
                  </span>
                </span>
              </div>
            </div>
          </>
        )}

        {tab === "prefs" && (
          <div className="detail-section">
            <div className="detail-section-title">Préférences</div>
            <div className="prop-row">
              <span className="prop-label">Thème</span>
              <span className="prop-value">
                <Segmented
                  options={[
                    { value: "light", label: "Clair" },
                    { value: "dark", label: "Sombre" },
                    { value: "auto", label: "Auto" },
                  ]}
                  value={prefs.theme}
                  onChange={(theme) => setPrefs({ theme })}
                  ariaLabel="Thème"
                />
              </span>
            </div>
            <div className="prop-row">
              <span className="prop-label">Liste par défaut</span>
              <span className="prop-value">
                <select
                  style={selectStyle}
                  value={prefs.defaultListId ?? ""}
                  onChange={(e) =>
                    setPrefs({ defaultListId: e.target.value || null })
                  }
                  aria-label="Liste par défaut"
                >
                  <option value="">Aucune</option>
                  {(lists.data ?? []).map((l) => (
                    <option key={l.id} value={l.id}>
                      {l.name}
                    </option>
                  ))}
                </select>
              </span>
            </div>
            <div className="prop-row">
              <span className="prop-label">Visibilité par défaut</span>
              <span className="prop-value">
                <select
                  style={selectStyle}
                  value={prefs.defaultVisibility}
                  onChange={(e) =>
                    setPrefs({
                      defaultVisibility: e.target.value as TaskVisibility,
                    })
                  }
                  aria-label="Visibilité par défaut"
                >
                  <option value="private">Privée</option>
                  <option value="team">Équipe</option>
                  <option value="tenant">Tenant</option>
                </select>
              </span>
            </div>
            <div className="prop-row">
              <span className="prop-label">Priorité par défaut</span>
              <span className="prop-value">
                <select
                  style={selectStyle}
                  value={prefs.defaultPriority}
                  onChange={(e) =>
                    setPrefs({ defaultPriority: e.target.value as Priority })
                  }
                  aria-label="Priorité par défaut"
                >
                  {Object.entries(PRIO).map(([k, v]) => (
                    <option key={k} value={k}>
                      {v.label}
                    </option>
                  ))}
                </select>
              </span>
            </div>
            <div className="prop-row">
              <span className="prop-label">Début de semaine</span>
              <span className="prop-value">
                <select
                  style={selectStyle}
                  value={prefs.weekStart}
                  onChange={(e) =>
                    setPrefs({ weekStart: e.target.value as "mon" | "sun" })
                  }
                  aria-label="Début de semaine"
                >
                  <option value="mon">Lundi</option>
                  <option value="sun">Dimanche</option>
                </select>
              </span>
            </div>
            <p style={{ fontSize: 11, color: "var(--ink-ghost)" }}>
              Préférences stockées localement sur ce navigateur.
            </p>
          </div>
        )}

        {tab === "notifs" && (
          <div className="detail-section">
            <div className="detail-section-title">Notifications</div>
            {NOTIF_TOGGLES.map((n) => (
              <label
                key={n.key}
                className="prop-row"
                style={{ alignItems: "center", cursor: "pointer" }}
              >
                <input
                  type="checkbox"
                  checked={prefs.notifs[n.key]}
                  onChange={(e) =>
                    setPrefs({
                      notifs: { ...prefs.notifs, [n.key]: e.target.checked },
                    })
                  }
                  aria-label={n.label}
                />
                <span className="prop-value" style={{ marginLeft: 8 }}>
                  {n.label}
                  {!n.live && (
                    <span className="tag" style={{ marginLeft: 6 }}>
                      inbox IC à venir
                    </span>
                  )}
                </span>
                <span
                  className="mono"
                  style={{
                    marginLeft: "auto",
                    fontSize: 10,
                    color: "var(--ink-ghost)",
                  }}
                >
                  {n.event}
                </span>
              </label>
            ))}
            <p style={{ fontSize: 11, color: "var(--ink-ghost)" }}>
              Les trois premiers filtrent les notifications dérivées localement
              ; les autres s'appliqueront à l'inbox du Notification Engine.
            </p>
          </div>
        )}
      </div>
    </>
  );
}
